import React, {useEffect, useState, useRef, useCallback} from 'react';
import { Button, Checkbox, Container, Grid, List, ListItem, ListItemText } from '@mui/material';
import { styled } from "@mui/system";
import MidiOutSelector from "./MidiOutSelector";
import MidiInSelector from "./MidiInSelector";
import DeviceIdSelector from "./DeviceIdSelector";
import { calcChecksum, getAddressByPatchNumber, getPatchNumberByAddress } from "./utils";
import {Overlay} from "./Overlay";

const StyledContainer = styled(Container)({
    position: 'fixed',
    top: 'calc(48px)',
    height: 'calc(100vh - 48px)',
    overflow: 'auto',
});

function SelectPatchesList({ selected, setSelected }) {
    const items = Array.from({ length: 101 }, (_, index) => `UA${index + 1}`).concat(
        Array.from({ length: 101 }, (_, index) => `UB${index + 1}`)
    );

    const handleToggle = (value) => () => {
        const currentIndex = selected.indexOf(value);
        const newSelected = [...selected];

        if (currentIndex === -1) {
            newSelected.push(value);
        } else {
            newSelected.splice(currentIndex, 1);
        }

        setSelected(newSelected);
    };

    return (
        <List>
            {items.map((value, index) => (
                <ListItem
                    key={index}
                    role={undefined}
                    dense
                    onClick={handleToggle(value)}
                >
                    <Checkbox
                        edge="start"
                        checked={selected.indexOf(value) !== -1}
                        tabIndex={-1}
                        disableRipple
                        style={{ height: '5px' }}
                    />
                    <ListItemText primary={value}/>
                </ListItem>
            ))}
        </List>
    );
}

export default function SavePatch({ midiOutputs, midiInputs }) {
    const [selected, setSelected] = useState([]);
    const [midiOut, setMidiOut] = useState(null);
    const [midiOutId, setMidiOutId] = useState(() => {
        const savedData = localStorage.getItem('midiOutIdSave');
        return savedData ? JSON.parse(savedData) : null;
    });
    const [deviceId, setDeviceId] = useState(() => {
        const savedData = localStorage.getItem('deviceIdSave');
        const number = parseInt(savedData ? JSON.parse(savedData) : 1);
        return number >= 0 && number < 32 ? number : 0;
    });
    const [midiInId, setMidiInId] = useState(() => {
        const savedData = localStorage.getItem('midiInIdSave');
        return savedData ? JSON.parse(savedData) : null;
    });
    const [midiIn, setMidiIn] = useState(null);
    const recieveTimeoutId = useRef(null);
    const [recievedPatchData, setRecievedPatchData] = useState({});
    const [isSaveSinglePatchMode, setIsSaveSinglePatchMode] = useState(false);
    const [isSaveAllPatchesMode, setIsSaveAllPatchesMode] = useState(false);


    const getMIDIMessage = useCallback((message) => {
        const address = message.data.slice(6, 9);
        const patchNumber = getPatchNumberByAddress(address);
        setRecievedPatchData((prevRecievedPatchData) => {
            const patchData = prevRecievedPatchData[patchNumber] || {};
            const newPatchData = {
                ...patchData,
                [address]: message.data.slice(9, -2),
            };
            return {
                ...prevRecievedPatchData,
                [patchNumber]: newPatchData,
            };
        });

        if (recieveTimeoutId.current) {
            clearTimeout(recieveTimeoutId.current); // 既存のタイムアウトをクリア
        }

        // 3秒後に実行する新しいタイムアウトをセット
        recieveTimeoutId.current = setTimeout(() => {
            saveRecievedPatchData();
        }, 1000);
    }, []);

    useEffect(() => {
        if (midiIn) {
            midiIn.addEventListener('midimessage', getMIDIMessage);
            return () => {
                midiIn.removeEventListener('midimessage', getMIDIMessage);
                if (recieveTimeoutId.current) {
                    clearTimeout(recieveTimeoutId.current);
                }
            };
        }
    }, [getMIDIMessage, midiIn]);

    useEffect(() => {
        localStorage.setItem('midiOutIdSave', JSON.stringify(midiOutId));
    }, [midiOutId]);

    useEffect(() => {
        localStorage.setItem('deviceIdSave', JSON.stringify(deviceId));
    }, [deviceId]);

    useEffect(() => {
        localStorage.setItem('midiInIdSave', JSON.stringify(midiInId));
    }, [midiInId]);

    const handleSavePatches = () => {
        console.log(deviceId);
        if (selected.length === 0) {
            alert('パッチを選択してください');
            return;
        }
        setRecievedPatchData({});
        setIsSaveSinglePatchMode(true);

        selected.forEach((patch) => {
            const patchAddress = getAddressByPatchNumber(patch);
            const patchAdrdressSize = [...patchAddress, 0x00, 0x04, 0x00];
            const patchDataRequestMessage = [0xF0, 0x41, deviceId, 0x00, 0x23, 0x11, ...patchAdrdressSize, calcChecksum(patchAdrdressSize), 0xF7];

            midiOut.send(patchDataRequestMessage);
        });

        recieveTimeoutId.current = setTimeout(() => {
            alert("データを受信しませんでした。\n同じVF-1に接続されたOutputデバイスとInputデバイスを選択されているか、VF-1で設定されたDevice IDが選択されているか、VF-1がBULK LOADモードになっているか確認してください。");
            clearMode();
        }, 1000);
    }

    const handleSaveAll = () => {
        setRecievedPatchData({});
        setIsSaveAllPatchesMode(true);

        const patchAddress = [0x00, 0x00, 0x00];
        const patchAdrdressSize = [...patchAddress, 0x07, 0x14, 0x00];
        const patchDataRequestMessage = [0xF0, 0x41, deviceId, 0x00, 0x23, 0x11, ...patchAdrdressSize, calcChecksum(patchAdrdressSize), 0xF7];

        midiOut.send(patchDataRequestMessage);

        recieveTimeoutId.current = setTimeout(() => {
            alert("データを受信しませんでした。\n同じVF-1に接続されたOutputデバイスとInputデバイスを選択されているか、VF-1で設定されたDevice IDが選択されているか、VF-1がBULK LOADモードになっているか確認してください。");
            clearMode();
        }, 1000);
    }

    function saveRecievedPatchData() {
        setRecievedPatchData((prevRecievedPatchData) => {
            setIsSaveSinglePatchMode((prevIsSaveSinglePatchMode) => {
                setIsSaveAllPatchesMode((prevIsSaveAllPatchesMode) => {
                    if (prevIsSaveSinglePatchMode) {
                        Object.keys(prevRecievedPatchData).forEach((key) => {
                            const patchData = prevRecievedPatchData[key];
                            const jsonString = JSON.stringify(patchData);
                            const blob = new Blob([jsonString], {type: 'application/json'});
                            const link = document.createElement('a');
                            link.href = URL.createObjectURL(blob);
                            link.download = 'VF1CON_PATCH_' + key + '.json';
                            link.click();
                        });
                    } else if (prevIsSaveAllPatchesMode) {
                        const jsonString = JSON.stringify(prevRecievedPatchData);
                        const blob = new Blob([jsonString], {type: 'application/json'});
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = 'VF1CON_PATCH_ALL.json';
                        link.click();
                    }
                    return false;
                });
                return false;
            });
            return {};
        });
    }

    function clearMode() {
        setIsSaveSinglePatchMode(false);
        setIsSaveAllPatchesMode(false);
        setRecievedPatchData({});
    }

    return (
        <div style={{ opacity: (isSaveSinglePatchMode || isSaveAllPatchesMode) ? 0.5 : 1, pointerEvents: (isSaveSinglePatchMode || isSaveAllPatchesMode) ? 'none' : 'auto' }}>
            <StyledContainer>
                <Grid container spacing={0} style={{ height: '100%'}}>
                    <Grid item xs={3} style={{ height: '100%', overflowY: 'auto' }}>
                        <div style={{ height: '50px' }}> {/* このdiv内にボタンを配置 */}
                            <Button variant="contained" color="primary" style={{ marginTop: '10px', width: '80%', height: 'calc(100% - 10px)' }} onClick={() => setSelected([])}>
                                選択をクリア
                            </Button>
                        </div>
                        <div style={{ height: 'calc(100% - 50px)', overflowY: 'auto' }}>
                            <SelectPatchesList selected={selected} setSelected={setSelected}/>
                        </div>
                    </Grid>
                    <Grid item xs={9} style={{ height: '100%', paddingTop: '30px', whiteSpace: 'pre-line', }}>
                        {"同じVF-1に接続されたOutputデバイスとInputデバイスを選択してください。\nまたVF-1をBULK LOADモードにしてください。"}
                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={6} >
                                <MidiOutSelector setMidiOut={setMidiOut} initialId={midiOutId} setInitialId={setMidiOutId} midiOutputs={midiOutputs}/>
                                <MidiInSelector setMidiIn={setMidiIn} initialId={midiInId} setInitialId={setMidiInId} midiInputs={midiInputs}/>
                                <DeviceIdSelector deviceId={deviceId} setCDeviceId={setDeviceId}/>
                            </Grid>
                            <Grid item xs={9} >
                                <Button variant="contained" color="primary" sx={{ textTransform: 'none' }} onClick={handleSavePatches} style={{ marginTop: '30px', width: '60%' }} disabled={!midiOut || !midiIn}>
                                    選択されたPatchを保存
                                </Button>
                            </Grid>
                            <Grid item xs={9} >
                                <Button variant="contained" color="primary" sx={{ textTransform: 'none' }} onClick={handleSaveAll} style={{ marginTop: '30px', width: '60%' }} disabled={!midiOut || !midiIn}>
                                    全てのUser Patchを1ファイルに保存
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </StyledContainer>
            {(isSaveSinglePatchMode || isSaveAllPatchesMode) && <Overlay>Patchデータ受信中... {Math.floor(Object.keys(recievedPatchData).length * 100 / (isSaveAllPatchesMode ? 202 : isSaveSinglePatchMode ? selected.length : 1)) + "%"}</Overlay>}
        </div>
    );
}